/** @jsx jsx */

import { Colors, Icon, Shadows, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Link from 'app/common/components/Link';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import { PartnerUserInteractionEventTracker } from 'app/developer-v3/teams/team-partner-sharing/createInteractionTracker';

import ClientIDAndSecret from './ClientIDAndSecret';
import PoweredByZapierTos from './PoweredByZapierTos';
import { PARTNER_SOLUTIONS_BASE_DOC_URL } from 'app/developer-v3/constants';

const styleContainer = css`
  display: flex;
  gap: 25px;
  margin-top: 40px;
`;

const styleCard = css`
  display: flex;
  flex-direction: column;
  padding: 25px 40px;
  border: 1px solid ${Colors.GrayWarm3};
  border-radius: 8px;
  box-shadow: ${Shadows.elevation5};
`;

const styleLink = css`
  display: flex;
  align-self: flex-start;
  gap: 5px;
  align-items: center;
  font-weight: 700;
`;

type Props = {
  slug: string;
  trackInteraction: PartnerUserInteractionEventTracker;
};

function Credentials({ trackInteraction, slug }: Props) {
  return (
    <ManagerLayout title="Credentials">
      <PoweredByZapierTos />
      <Text margin="-10px 0 20px" tag="p">
        Your client ID and client secret are used to identify your app in the
        Workflow API and the Workflow Element. It returns details for your
        integration from Zapier. This includes your Zap templates and Zaps that
        your users created with your integration.
      </Text>
      <ClientIDAndSecret slug={slug} trackInteraction={trackInteraction} />
      <div css={styleContainer}>
        <div css={styleCard}>
          <Text tag="h2" type="Body3">
            Workflow API
          </Text>
          <Text margin="10px 0" tag="p">
            Use your client ID and client secret to access data about your Zap
            templates through the Workflow API. You will be able to style your
            Zap templates to fit your brand and UI.
          </Text>
          <Link
            css={styleLink}
            href={`${PARTNER_SOLUTIONS_BASE_DOC_URL}/workflow-api/intro`}
            target="_blank"
          >
            Get started
            <Icon name="arrowOffsite" size={16} />
          </Link>
        </div>

        <div css={styleCard}>
          <Text tag="h2" type="Body3">
            Workflow Element
          </Text>
          <Text margin="10px 0" tag="p">
            Use your client ID and client secret to generate and embed the
            Workflow Element in your product. Enable your users to discover,
            create, and edit their Zaps inside your app and build workflows
            without distractions.
          </Text>
          <Link
            css={styleLink}
            href="https://zapier.com/partner/embed/workflow"
            target="_blank"
          >
            Get started
            <Icon name="arrowOffsite" size={16} />
          </Link>
        </div>
      </div>
    </ManagerLayout>
  );
}

export default Credentials;
